import { Dispatch, SetStateAction, useEffect } from "react";
import { loadCountryConfigFiles } from "../countryConfigLoader";
import { FormPayTypeValue } from "pages/employer-of-record/payments/constants";
import { addDisbursementDate } from "pages/employer-of-record/payments/utils";
import { AdditionalPayContextProps } from "pages/employer-of-record/payments/contexts/additionalPayContext";
import { FieldConfig } from "../../SetupFormGenerator";
import { CountryConfigKeyType, SubdivisionConfigKeyType } from "../types";
import { FormPayloadType } from "../../../types";

function useCountryConfigFetcher<T extends FormPayloadType = FormPayloadType>(
  type: FormPayTypeValue,
  additionalPayContext: AdditionalPayContextProps | null,
  setPaymentSettingsFields: Dispatch<SetStateAction<FieldConfig<T>[]>>,
  setFormFields?: Dispatch<SetStateAction<FieldConfig<T>[]>>
): void {
  const selectedCountry = additionalPayContext?.selectedCountry;
  const selectedSubdivision = additionalPayContext?.selectedSubdivision;
  const isLocationReady = additionalPayContext?.isLocationReady ?? false;

  useEffect(() => {
    const fetchCountryConfigs = () => {
      if (!isLocationReady) return;

      const configs = loadCountryConfigFiles(
        type,
        selectedCountry as CountryConfigKeyType,
        selectedSubdivision as SubdivisionConfigKeyType | undefined
      );
      if (!configs) {
        setPaymentSettingsFields([]);
        setFormFields?.([]);
        return;
      }
      let paymentSettingsFormFields = configs.PAYMENT_SETTINGS_FORM_FIELDS;
      if (additionalPayContext?.disbursementDateOptions) {
        paymentSettingsFormFields = addDisbursementDate(
          paymentSettingsFormFields,
          Object.values(additionalPayContext.disbursementDateOptions)
        );
      }
      setPaymentSettingsFields(paymentSettingsFormFields);
      if (setFormFields && "TAX_FORM_FIELDS" in configs) {
        // This can either have complex type narrowing,
        // or we can just cast it to FieldConfig[]
        setFormFields(
          (configs as Record<string, unknown>)[
            "TAX_FORM_FIELDS"
          ] as FieldConfig<T>[]
        );
      }
    };

    fetchCountryConfigs();
  }, [
    isLocationReady,
    selectedCountry,
    selectedSubdivision,
    additionalPayContext?.disbursementDateOptions,
    setFormFields,
    setPaymentSettingsFields,
    type,
  ]);
}

export default useCountryConfigFetcher;
